import { normalizeActivityFrame, normalizeResolution, type CompatibleResolution } from '../lib/normalized-activity';
import type { AgentCapabilities, AgentRuntimeInfo, AgentInteractionRequest, ProjectedTimelineEntry } from '@borgee/agent-remote-protocol';
import { create } from 'zustand';
import type { NormalizedActivityFrame } from '../types/ws-frames';
import { DshSessionProjector } from '../lib/dsh-session-projection';

export interface NormalizedItemReference {
  taskId: string;
  entry: ProjectedTimelineEntry;
}

export type { SessionTaskPresentation as NormalizedTaskState } from '@borgee/remote-control-client';
import type { SessionTaskPresentation as NormalizedTaskState } from '@borgee/remote-control-client';

type ChannelState = Record<string, Record<string, Record<string, NormalizedTaskState>>>;
interface TaskLocation { agentId: string; taskId: string }
type ResolutionHandler = (taskId: string, resolution: CompatibleResolution, token: string) => void;
interface NormalizedActivityState {
  byChannel: ChannelState;
  noticesByChannel: Record<string, string | undefined>;
  taskOrderByChannel: Record<string, TaskLocation[]>;
  lastSequenceByAgent: Record<string, number>;
  applyPresentation(channelId: string, agentId: string, tasks: Record<string, NormalizedTaskState>, notice?: string, resolve?: ResolutionHandler): void;
  applyFrame(frame: NormalizedActivityFrame, receivedAt?: number): void;
  applyResolution(channelId: string, agentId: string, taskId: string, resolution: CompatibleResolution, expectedSequence?: number | string): void;
  suspendAgent(agentId: string): void;
  suspendAll(): void;
  clearAgent(agentId: string): void;
  clearChannel(channelId: string): void;
  reset(): void;
}
interface CompatibilityModel { channelId: string; agentId: string; projector: DshSessionProjector; generation?: string; ordinal: number; offset: number }
const compatibility = new Map<string, CompatibilityModel>();
const resolvers = new Map<string, ResolutionHandler>();
const identity = (channelId: string, agentId: string) => JSON.stringify([channelId, agentId]);

/** Session admission owns presentation; the main chat mirrors its complete bounded snapshot. */
export const useNormalizedActivityStore = create<NormalizedActivityState>((set, get) => {
  const publish = (model: CompatibilityModel) => get().applyPresentation(model.channelId, model.agentId, model.projector.taskSnapshot(model.generation), model.projector.view().capacityNotice);
  const suspend = (agentId?: string) => {
    for (const model of compatibility.values()) if (!agentId || model.agentId === agentId) { model.generation = undefined; model.offset = model.ordinal; }
    set(state => {
      const byChannel = structuredClone(state.byChannel);
      for (const agents of Object.values(byChannel)) for (const [id, tasks] of Object.entries(agents)) {
        if (agentId && id !== agentId) continue;
        for (const task of Object.values(tasks)) {
          task.active = false; task.terminal = false; task.nativeCapabilitiesLive = false;
          for (const pending of task.pendingInteractions ?? []) pending.expired = true;
        }
      }
      const lastSequenceByAgent = agentId ? { ...state.lastSequenceByAgent } : {};
      if (agentId) delete lastSequenceByAgent[agentId];
      return { byChannel, lastSequenceByAgent };
    });
  };
  return {
    byChannel: {}, noticesByChannel: {}, taskOrderByChannel: {}, lastSequenceByAgent: {},
    applyPresentation(channelId, agentId, tasks, notice, resolve) {
      if (resolve) resolvers.set(identity(channelId, agentId), resolve);
      set(state => {
        const agents = { ...state.byChannel[channelId], [agentId]: structuredClone(tasks) };
        if (!Object.keys(tasks).length) delete agents[agentId];
        return { byChannel: { ...state.byChannel, [channelId]: agents },
          noticesByChannel: { ...state.noticesByChannel, [channelId]: notice ?? state.noticesByChannel[channelId] },
          taskOrderByChannel: { ...state.taskOrderByChannel, [channelId]: Object.entries(agents).flatMap(([id, values]) => Object.keys(values).map(taskId => ({ agentId: id, taskId }))) } };
      });
    },
    applyFrame(input, receivedAt = Date.now()) {
      const frame = normalizeActivityFrame(input);
      if (!frame) return;
      const previous = get().lastSequenceByAgent[frame.agent_id];
      if (previous !== undefined && frame.sequence <= previous) return;
      const key = identity(frame.channel_id, frame.agent_id);
      let model = compatibility.get(key);
      if (!model) { model = { channelId: frame.channel_id, agentId: frame.agent_id, projector: new DshSessionProjector(), ordinal: 0, offset: 0 }; compatibility.set(key, model); }
      model.generation ??= crypto.randomUUID();
      model.ordinal = Math.max(model.ordinal + 1, model.offset + frame.sequence);
      const record = { id: crypto.randomUUID(), generation: model.generation, ordinal: model.ordinal, receivedAt, frame };
      if (!model.projector.taskIds().includes(frame.task_id)) {
        const locations = [...compatibility.values()].filter(value => value.channelId === frame.channel_id).flatMap(value => value.projector.taskIds().map(taskId => ({ model: value, taskId })));
        if (locations.length >= 16) {
          const victim = locations.find(value => !value.model.projector.hasRequests(value.taskId));
          if (victim) { victim.model.projector.removeTask(victim.taskId); publish(victim.model); }
          else { model.projector.rejectTask(record); publish(model); set(state => ({ lastSequenceByAgent: { ...state.lastSequenceByAgent, [frame.agent_id]: frame.sequence } })); return; }
        }
      }
      model.projector.append(record); publish(model);
      set(state => ({ lastSequenceByAgent: { ...state.lastSequenceByAgent, [frame.agent_id]: frame.sequence } }));
    },
    applyResolution(channelId, agentId, taskId, resolution, expectedSequence) {
      const normalized = normalizeResolution(resolution);
      if (!normalized || normalized.payload.agentId !== agentId) return;
      const pending = get().byChannel[channelId]?.[agentId]?.[taskId]?.pendingInteractions?.find(value => !value.expired && value.request.requestId === normalized.payload.requestId && value.request.kind === normalized.payload.response.kind && (expectedSequence === undefined || (typeof expectedSequence === 'string' ? value.token === expectedSequence : (value.displaySequence ?? value.sequence) === expectedSequence)));
      if (!pending?.token) return;
      const resolve = resolvers.get(identity(channelId, agentId));
      if (resolve) { resolve(taskId, normalized, pending.token); return; }
      const model = compatibility.get(identity(channelId, agentId));
      const original = model?.projector.pendingRecord(pending.token);
      if (!model || !original) return;
      model.projector.append({ ...original, id: crypto.randomUUID(), ordinal: ++model.ordinal, resolution: normalized }); publish(model);
    },
    suspendAgent: suspend,
    suspendAll: () => suspend(),
    clearAgent(agentId) {
      for (const [key, model] of compatibility) if (model.agentId === agentId) compatibility.delete(key);
      for (const key of resolvers.keys()) if ((JSON.parse(key) as string[])[1] === agentId) resolvers.delete(key);
      set(state => {
        const byChannel = structuredClone(state.byChannel);
        const taskOrderByChannel = { ...state.taskOrderByChannel };
        for (const [channelId, agents] of Object.entries(byChannel)) { delete agents[agentId]; taskOrderByChannel[channelId] = (taskOrderByChannel[channelId] ?? []).filter(task => task.agentId !== agentId); }
        const lastSequenceByAgent = { ...state.lastSequenceByAgent }; delete lastSequenceByAgent[agentId];
        return { byChannel, taskOrderByChannel, lastSequenceByAgent };
      });
    },
    clearChannel(channelId) {
      for (const [key, model] of compatibility) if (model.channelId === channelId) compatibility.delete(key);
      for (const key of resolvers.keys()) if ((JSON.parse(key) as string[])[0] === channelId) resolvers.delete(key);
      set(state => {
        const byChannel = { ...state.byChannel }; delete byChannel[channelId];
        const taskOrderByChannel = { ...state.taskOrderByChannel }; delete taskOrderByChannel[channelId];
        const noticesByChannel = { ...state.noticesByChannel }; delete noticesByChannel[channelId];
        return { byChannel, taskOrderByChannel, noticesByChannel };
      });
    },
    reset() { compatibility.clear(); resolvers.clear(); set({ byChannel: {}, noticesByChannel: {}, taskOrderByChannel: {}, lastSequenceByAgent: {} }); },
  };
});

export function normalizedDetailItems(tasks: Record<string, NormalizedTaskState> | undefined): NormalizedItemReference[] {
  return Object.entries(tasks ?? {}).flatMap(([taskId, task]) => task.entries.map(entry => ({ taskId, entry })));
}
